// vitest からは Nuxt auto-import が効かないため明示 import に。
import { readonly, ref, watchEffect, type Ref } from 'vue'
import { createError } from 'h3'
import {
  createOrderEntry,
  createOrderLoanAplusApplicationUrl,
  createOrderLoanJaccsApplicationUrl,
  createOrderPayeasyUrl,
  createOrderPaypayUrl,
  payOrderByCreditCard,
} from './api/order-entry'
import { isAccountMismatchError, useOrder } from './order'
import { useStore } from './store'
import type { Order, OrderCalculationBase } from './order'
import type { SubProfileTarget } from './sub-profile'
import { storage } from '~/utils/session-storage'
import { useBJP } from '~/utils/bjp'
import { useJaccs } from '~/utils/loan-jaccs'
import { useMath } from '~/utils/math'
import { FieldEnum as PaymentMethodFieldEnum } from '~/utils/payment-method'
import type {
  ApplyLoanAplusUrlOrder201,
  PayCreditCardOrder200,
  PayPaypayUrlOrder201,
  PostOrderList201,
} from '~/types/api/Order'
import type { CamelCaseKeys } from '~/types/util'

export type CreatedOrder = CamelCaseKeys<PostOrderList201>
export type CompletedOrder = CamelCaseKeys<PayCreditCardOrder200>
export type PaypayUrlResponse = CamelCaseKeys<PayPaypayUrlOrder201>
export type AplusApplicationUrlResponse = CamelCaseKeys<ApplyLoanAplusUrlOrder201>

// 簡易会計の create は公開店舗応答(かかりつけ非依存)を元にする。かかりつけ確立は
// orders#create 側が選択対象者に対してのみ行う(画面を開いた時点では紐付けない)。
const { store } = useStore()

// 顧客主導申込は税込金額で入力するため、税計算時の基準を「税込」で固定している。
export const calculationBaseForIndependent: OrderCalculationBase = 'total-amount'

export interface OrderCreateRequestParam {
  storeIdAlias: string
  name: string
  isLoanAvailable: boolean
  calculationBase: OrderCalculationBase
  amount: number
  subtotalAmount: number
  taxAmount: number // API上は連携不要だが、Frontとしては整合性担保のため許容しない。
  totalAmount: number // API上は連携不要だが、Frontとしては整合性担保のため許容しない。
  // 代理会計の宛先。簡易会計で選択した対象者(本人/メンバー)の pgm id。未指定=操作者自身。
  targetGroupMemberId?: number
}

// BJPへ送信するKeyをのままとするためkeyはsnakeのまま扱う。
export interface CreatePaymentUrlByPayeasyResopnseParam {
  url: string
  form_data: {
    PORTAL_CODE: string
    SHOP_CODE: string
    BANK_CODE: string
    KESSAI_FLAG: string
    CTRL_NO: string
    TRAN_AMOUNT: number
    VALID_RETURN_URL: string
    INVALID_RETURN_URL: string
    CUST_NAME: string
    CUST_LNAME: string
    CUST_FNAME: string
    REMARKS_1: string
  }
}

export interface CreateApplicationUrlByLoanJaccsRequestParam {
  interestType: 'interest' | 'no-interest'
}

// Jaccsへ送信するKeyをのままとするためkeyはsnakeのまま扱う。
export interface CreateApplicationUrlByLoanJaccsResopnseParam {
  url: string
  form_data: {
    KAMEITEN_BANGO: string
    DENPYO_BANGO: string
    NAME: string
    NAME_KANA: string
    SEIBETU: number
    SEINEN_YEAR: number
    SEINEN_MONTH: number
    SEINEN_DATE: number
    TEL: string
    EMAIL: string
    DELIVERY_ZIPCODE: string
    DELIVERY_ADDRESS: string
    SINA_01_SINAMEI: string
    SINA_01_SURYOU: string
    SINA_01_TANI: string
    SINA_01_KINGAKU: number
    SINA_01_SINACODE: string
    SYOHIZEI: string
    SOURYOU: string
    GOUKEIGAKU: number
    EKIMUKIKAN_FROM: string
    EKIMUKIKAN_TO: string
    EKIMU_KUBUN: string
    RETURN_URL: string
    CANCEL_RETURN_URL: string
  }
}

export interface OrderEntryIndependentParams {
  name: string
  totalAmount: number
}

export interface OrderEntry {
  selectedPaymentMethod?: PaymentMethodFieldEnum
  // GET /orders/:id 経由で取得済の rich な Order。input() でセットされる。
  order?: Order
  // POST /orders 直後の slim な作成結果 (store / details などは持たない)。
  // entryIndependent フローでのみセットされる。
  createdOrder?: CreatedOrder
  independentParams?: OrderEntryIndependentParams
  loanJaccsParams?: CreateApplicationUrlByLoanJaccsRequestParam
  orderLinkToken?: string
  // 会計URLに載る新規患者(glen未リンク)用トークン。select-target で選択対象者の
  // かかりつけ確立(related_stores#link)に使う。link_with_store! が当該医院の waiting order を
  // 対象者へ back-fill する(=所有者割当)ため、これがあるときは orders#link は呼ばない。
  customerLinkToken?: string
  completedOrder?: CompletedOrder
  // 会計の対象者(本人/メンバー)。select-target で選択し pay-method の対象者表示に使う。
  selectedTarget?: SubProfileTarget
}

const customStorage = storage<OrderEntry>('orderEntry')
const orderEntry = ref(customStorage.getItem()) as Ref<OrderEntry>
watchEffect(() => customStorage.setItem(orderEntry.value))

const input = ({
  selectedPaymentMethod,
  order,
  independentParams,
  loanJaccsParams,
  orderLinkToken,
  customerLinkToken,
  selectedTarget,
}: OrderEntry) => {
  if (selectedPaymentMethod) orderEntry.value.selectedPaymentMethod = selectedPaymentMethod
  if (order) orderEntry.value.order = order
  if (independentParams) orderEntry.value.independentParams = independentParams
  if (loanJaccsParams) orderEntry.value.loanJaccsParams = loanJaccsParams
  if (orderLinkToken) orderEntry.value.orderLinkToken = orderLinkToken
  if (customerLinkToken) orderEntry.value.customerLinkToken = customerLinkToken
  if (selectedTarget) orderEntry.value.selectedTarget = selectedTarget
  return readonly(orderEntry)
}

const entry = async () => {
  switch (orderEntry.value.selectedPaymentMethod) {
    case PaymentMethodFieldEnum.creditCard: {
      const completedOrder = await payByCreditCard()
      complete()
      orderEntry.value.completedOrder = completedOrder
      break
    }
    case PaymentMethodFieldEnum.paypay: {
      const paymentUrl = await createPaymentUrlByPaypay()
      linkToPaypay(paymentUrl)
      complete()
      return
    }
    case PaymentMethodFieldEnum.payeasy: {
      const params = await createPaymentUrlByPayeasy()
      linkToPayeasy(params)
      complete()
      return
    }
    case PaymentMethodFieldEnum.loanJaccs: {
      const params = await createApplicationUrlByLoanJaccs()
      linkToJaccs(params)
      complete()
      return
    }
    case PaymentMethodFieldEnum.loanAplus: {
      const applicationUrl = await createApplicationUrlByLoanAplus()
      linkToAplus(applicationUrl)
      // アプラスはURLが1回限りなので、ブラウザバックで再生成できるよう、ここでは reset しない。
      // reset は戻り先URLの /entry/loan-aplus/complete が行う。
      return
    }
    default:
      throw createError({
        statusCode: 400,
        statusMessage: '不正な操作が行われました。',
        fatal: true,
      })
  }
}

const entryIndependent = async () => {
  const { calculateSubtotalAmountByTotalAmount, calculateTaxAmountByTotalAmount } = useMath()
  const currentTotalAmount = getCurrentTotalAmount()!
  const isLoanAvailable = store.value.enabledPaymentMethods.some(
    (v) => v === 'loan-jaccs' || v === 'loan-aplus'
  )

  const order = await create({
    storeIdAlias: store.value.idAlias,
    name: getCurrentName(),
    calculationBase: calculationBaseForIndependent,
    amount: currentTotalAmount,
    isLoanAvailable,
    subtotalAmount: calculateSubtotalAmountByTotalAmount(currentTotalAmount),
    taxAmount: calculateTaxAmountByTotalAmount(currentTotalAmount),
    totalAmount: currentTotalAmount,
    targetGroupMemberId: orderEntry.value.selectedTarget?.proxyGroupMemberId,
  })

  orderEntry.value.createdOrder = order
  orderEntry.value.orderLinkToken = order.linkToken
  await entry()
}

const currentOrderId = (): number => {
  const id = orderEntry.value.order?.id ?? orderEntry.value.createdOrder?.id
  if (id === undefined) throw new Error('order id is required but not available')
  return id
}

const linkToPaypay = (paymentUrl: string) => {
  navigateTo(paymentUrl, { external: true })
}

const linkToPayeasy = (params: CreatePaymentUrlByPayeasyResopnseParam) => {
  useBJP().submitForm(params)
}

const linkToJaccs = (params: CreateApplicationUrlByLoanJaccsResopnseParam) => {
  useJaccs().submitForm(params)
}

const linkToAplus = (paymentUrl: string) => {
  navigateTo(paymentUrl, { external: true })
}

const getCurrentName = (): string => {
  return (
    orderEntry.value.independentParams?.name ??
    orderEntry.value.order?.name ??
    orderEntry.value.createdOrder?.name ??
    ''
  )
}

const getCurrentTotalAmount = (): number | undefined => {
  return (
    orderEntry.value.independentParams?.totalAmount ??
    orderEntry.value.order?.totalAmount ??
    orderEntry.value.createdOrder?.totalAmount ??
    undefined
  )
}

const isIndependent = () => {
  return Boolean(!useRoute().params.id)
}

// 確定処理のエラーを可視化する。会計URL/サブスク(id 有り)は前段で対象者確定済みのため挙動を
// 変えず想定外エラーは再 throw する。簡易会計(independent)の orders#create エラーは、宛先不一致
// (403)なら「アカウント間違い→再ログイン」としてかかりつけ pay-error へ、それ以外(404/422/5xx/
// 通信断)はアカウント間違いではないため汎用 error.vue へ寄せる。確定ボタン(event handler)由来では
// throw が無反応になるため、showError(createError)で error.vue を明示描画する。
const handleConfirmationError = async (error: unknown) => {
  if (!isIndependent()) throw error
  if (isAccountMismatchError(error)) {
    await navigateTo('/me/patient-card/pay-error/')
    return
  }
  showError(
    createError({
      statusCode: (error as { statusCode?: number })?.statusCode ?? 500,
      fatal: true,
    })
  )
}

// 以下API接続群はリソース的にはorder.tsになるが、
// OrderのEntry処理に他ならないのでこちらで定義してカプセル化してみた。
const create = async (params: OrderCreateRequestParam) => {
  return await createOrderEntry(params)
}
// 代理会計の宛先(本人/メンバー)の pgm id。select-target で保持済。本人選択時も実 pgm id が入り、backend は actor==target を短絡するため送って安全。
const currentTargetGroupMemberId = (): number | undefined =>
  orderEntry.value.selectedTarget?.proxyGroupMemberId

const payByCreditCard = async (): Promise<CompletedOrder> => {
  return await payOrderByCreditCard(currentOrderId(), currentTargetGroupMemberId())
}
const createPaymentUrlByPaypay = async () => {
  return (await createOrderPaypayUrl(currentOrderId(), currentTargetGroupMemberId())).url
}
const createPaymentUrlByPayeasy = async () => {
  return await createOrderPayeasyUrl(currentOrderId(), currentTargetGroupMemberId())
}
const createApplicationUrlByLoanJaccs = async () => {
  return await createOrderLoanJaccsApplicationUrl(
    currentOrderId(),
    orderEntry.value.loanJaccsParams,
    currentTargetGroupMemberId()
  )
}
const createApplicationUrlByLoanAplus = async () => {
  return (await createOrderLoanAplusApplicationUrl(currentOrderId(), currentTargetGroupMemberId()))
    .url
}

const reset = () => {
  orderEntry.value = {}
  customStorage.removeItem()
}

const complete = () => {
  reset()

  // 申し込み完了時、決済依頼の個数が必ず変わるので取得処理を挟む。
  // 裏で動かすだけなのでエラーになったとしてもハンドリングしない。
  useOrder().getRequests().catch(() => {})
}

export const useOrderEntry = () => {
  return {
    orderEntry: readonly(orderEntry) as Readonly<Ref<OrderEntry>>,
    getCurrentName,
    getCurrentTotalAmount,
    currentTargetGroupMemberId,
    input,
    entry,
    entryIndependent,
    isIndependent,
    handleConfirmationError,
    reset,
    complete,
  }
}
